/**
 * Seo — head-tag manager for public pages.
 *
 * Imperatively manages document.title, meta[name/property], link[rel=canonical]
 * and one application/ld+json script per page. This is more reliable across
 * environments than relying on React 19's implicit head-hoisting rules, and
 * lets us cleanly OVERRIDE the defaults already present in index.html.
 *
 * All tags managed by this component are marked with data-seo="1" so we can
 * safely upsert without clobbering third-party or router-external tags.
 */
import { useEffect } from "react";

const DEFAULT_SITE = "https://dailyexpense.online";
const DEFAULT_OG_IMAGE = `${DEFAULT_SITE}/icons/icon-512.png`;

/** Upsert a <meta> element by (attr, key). */
function upsertMeta(attr, key, value, ownedIds) {
  if (value == null || value === "") return;
  const selector = `meta[${attr}="${CSS.escape(key)}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  ownedIds.add(el);
}

/** Upsert a <link rel="canonical">. */
function upsertCanonical(href, ownedIds) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  ownedIds.add(el);
}

/** Remove all previously-owned tags that are ours AND multi-instance (article:tag). */
function purgeOwnedMultiTags() {
  document.head.querySelectorAll('meta[data-seo="1"][property="article:tag"]').forEach((n) => n.remove());
}

export default function Seo({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  article,
  jsonLd,
}) {
  useEffect(() => {
    const canonical = `${DEFAULT_SITE}${path || window.location.pathname}`;
    const ownedIds = new Set();

    if (title) document.title = title;

    upsertMeta("name", "description", description, ownedIds);
    upsertCanonical(canonical, ownedIds);

    // Open Graph
    upsertMeta("property", "og:type", type, ownedIds);
    upsertMeta("property", "og:site_name", "Expense & Save", ownedIds);
    upsertMeta("property", "og:title", title, ownedIds);
    upsertMeta("property", "og:description", description, ownedIds);
    upsertMeta("property", "og:url", canonical, ownedIds);
    upsertMeta("property", "og:image", image, ownedIds);

    // Article
    if (type === "article" && article) {
      upsertMeta("property", "article:published_time", article.publishedTime, ownedIds);
      upsertMeta("property", "article:modified_time",  article.modifiedTime,  ownedIds);
      upsertMeta("property", "article:author",         article.authorName,    ownedIds);
      upsertMeta("property", "article:section",        article.section,       ownedIds);

      // article:tag is multi-value; purge previous ours, then add fresh.
      purgeOwnedMultiTags();
      if (Array.isArray(article.tags)) {
        for (const t of article.tags) {
          const el = document.createElement("meta");
          el.setAttribute("property", "article:tag");
          el.setAttribute("content", t);
          el.setAttribute("data-seo", "1");
          document.head.appendChild(el);
        }
      }
    } else {
      // Leaving article page → clear any lingering article:* meta ours.
      document.head
        .querySelectorAll('meta[data-seo="1"][property^="article:"]')
        .forEach((n) => n.remove());
    }

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image", ownedIds);
    upsertMeta("name", "twitter:title", title, ownedIds);
    upsertMeta("name", "twitter:description", description, ownedIds);
    upsertMeta("name", "twitter:image", image, ownedIds);

    // JSON-LD (single per-page). Purge previous ours before adding new.
    document.head.querySelectorAll('script[data-seo-jsonld="1"]').forEach((n) => n.remove());
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }

    // Note: We intentionally do NOT remove the standard meta tags on unmount.
    // The next page mounts Seo and overwrites them, giving a smooth transition
    // with no flash of missing metadata. We only purge multi-value / JSON-LD.
  }, [title, description, path, image, type, article, jsonLd]);

  return null;
}
